import React, { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Empty, Input, Modal, Result, Segmented, Space, Spin, Tabs, Tag, Tooltip, Typography, theme } from 'antd';
import {
  CalendarOutlined, ClockCircleOutlined, EyeOutlined, RollbackOutlined, SendOutlined, UserOutlined,
} from '@ant-design/icons';
import HarcirahKabuk from './kabuk';
import { HaftalikPlan, SeyahatPenceresi } from './seyahat';
import { GiderBolumu, GiderPenceresi } from './giderler';
import { REZ_ASAMA_ADI, SeyahatAkisi, hata, para, tarih, useHarcirahApi } from './ortak';

/**
 * Rezervasyon ekranı (/harcirah/rezervasyon) — rezervasyon personeli (30.10.2026: Yönetici Asistanı, İdari İşler Uzmanı
 * ünvanları + 327; roller.rezervasyon). 26.10.2026 akış: seyahati personel planlar, amir onaylar;
 * rezervasyon personeli (1) fiyatları girer (belge şart değil) → finans fiyat onayı + limit (kart açar) → (2) ödemeyi
 * yapıp işlemleri tamamlar, belgeleri ekler → finans harcama kontrolü → yönetici. Konaklama gece gece; tek otelde
 * aralık seçilince geceler dolar. Yakıt fişlerini çalışan girer (salt okunur). Belge okumada seyahate gidenin açık
 * rızası aranır (yükleyen rezervasyon personeli olsa da).
 */
const { Text } = Typography;

const ASAMA_NOTU = {
  fiyat: 'Fiyat aşaması: aldığınız fiyatları girin; bilet / fatura şart değil, ödeme aşamasında eklenir.',
  islem: 'Ödeme ve işlem: ödemeyi yapıp her kalemin belgesini (bilet, fatura) ekleyin; sonra finans kontrolüne gönderin.',
};

function RezervasyonKarti({ s, onEkle, onDuzenle, onAyrinti, onIlerlet, onPlanaGeri }) {
  const bekleyen = s.rez_belge_bekleyen?.length || 0;
  const duzenlenebilir = s.rez_duzenlenebilir;
  const geri = [...s.gecmis].reverse().find((g) => ['rez_geri', 'rez_onay', 'rez_ilerlet'].includes(g.islem));
  const geriDondu = geri?.islem === 'rez_geri' && geri.hedef === s.rez_durum;
  return (
    <div className="rz-kart">
      <div className="rz-bas">
        <div style={{ minWidth: 0 }}>
          <Space size={8} wrap>
            <span className="rz-kisi">{s.personel_adi}</span>
            <span className="rz-il">{s.il_ad}</span>
            <Tag style={{ marginInlineEnd: 0 }}>{s.seyahat_turu_adi}</Tag>
            <Tag color={s.rez_durum === 'tamam' ? 'success' : duzenlenebilir ? 'gold' : 'processing'} style={{ marginInlineEnd: 0 }}>{s.rez_durum_adi}</Tag>
          </Space>
          <div className="rz-meta"><CalendarOutlined /> {tarih(s.baslangic)} – {tarih(s.bitis)} · {s.gunler.length} Gün · Tip {s.tip}</div>
          <div className="rz-meta"><UserOutlined /> Amir: {s.amir_adi}{s.aciklama ? ` · ${s.aciklama}` : ''}</div>
        </div>
        <div className="rz-sag">
          {bekleyen > 0
            ? <Tag color="warning" icon={<ClockCircleOutlined />} style={{ marginInlineEnd: 0, fontWeight: 600 }}>{bekleyen} Belge Bekleniyor</Tag>
            : s.rez_toplam > 0 && <Tag color="success" style={{ marginInlineEnd: 0 }}>Belgeler Tamam</Tag>}
          <div className="rz-meta">Rezervasyon: <b>{para(s.rez_toplam)}</b></div>
          {s.rez_limit != null && (
            <div className="rz-meta" style={{ color: s.limit_asimi ? 'var(--rz-hata)' : undefined, fontWeight: s.limit_asimi ? 600 : 400 }}>
              Finans limiti {para(s.rez_limit)}{s.limit_asimi ? ' · AŞILDI' : ''}
            </div>
          )}
          <Button size="small" icon={<EyeOutlined />} onClick={() => onAyrinti(s)}>Ayrıntı</Button>
        </div>
      </div>
      <div className="rz-govde">
        {s.rez_durum && <div style={{ marginBottom: 10 }}><SeyahatAkisi s={s} kompakt /></div>}
        {s.rez_notu && (
          <Alert type="warning" showIcon style={{ marginBottom: 10 }} message={<span><b>Personelin Talebi:</b> {s.rez_notu}</span>} />
        )}
        {geriDondu && (
          <Alert type="error" showIcon style={{ marginBottom: 10 }}
            message={`${REZ_ASAMA_ADI[geri.asama] || geri.asama} aşamasından geri döndü`}
            description={<span><b>{geri.kisi_adi}</b>{geri.vekil ? ` (test vekili, ${geri.yerine} yerine)` : ''}: <i>{geri.notu}</i></span>} />
        )}
        <GiderBolumu s={s} rol="rezervasyon" baslik={false} not={ASAMA_NOTU[s.rez_durum]}
          onEkle={duzenlenebilir ? (t, ek) => onEkle(s, t, ek) : undefined}
          onDuzenle={duzenlenebilir ? (gd) => onDuzenle(s, gd) : undefined} />
        {(s.rez_durum === 'fiyat' || s.rez_durum === 'islem') && (
          <div className="rz-alt">
            {s.rez_durum === 'fiyat' && <Button icon={<RollbackOutlined />} onClick={() => onPlanaGeri(s)}>Plana Geri Gönder</Button>}
            <Tooltip title={s.rez_durum === 'islem' && bekleyen ? 'Önce her kalemin belgesini ekleyin' : ''}>
              <Button type="primary" icon={<SendOutlined />} disabled={s.rez_durum === 'islem' && bekleyen > 0} onClick={() => onIlerlet(s)}>
                {s.rez_durum === 'fiyat' ? 'Finans Onayına Gönder' : 'Finans Kontrolüne Gönder'}
              </Button>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
}

function RezervasyonListesi({ secenekler }) {
  const api = useHarcirahApi('/harcirah-rezervasyon');
  const { message, modal } = App.useApp();
  const { token } = theme.useToken();
  const [kapsam, setKapsam] = useState('acik');
  const [neden, setNeden] = useState({ seyahat: null, metin: '' });
  const [liste, setListe] = useState(null);
  const [ara, setAra] = useState('');
  const [pencere, setPencere] = useState({ acik: false, id: null });
  const [giderIstek, setGiderIstek] = useState(null);

  const yukle = useCallback(() => {
    setListe(null);
    api.get('/liste', { kapsam }).then(setListe).catch((e) => { message.error(hata(e)); setListe([]); });
  }, [api, kapsam, message]);
  useEffect(() => { yukle(); }, [yukle]);

  const guncelle = (s) => setListe((l) => l.map((x) => (x.id === s.id ? s : x)));
  const ilerlet = (s) => modal.confirm({
    title: s.rez_durum === 'fiyat' ? 'Fiyatlar finans onayına gönderilsin mi?' : 'Harcamalar finans kontrolüne gönderilsin mi?',
    content: `${s.personel_adi} · ${s.il_ad} · rezervasyon ${para(s.rez_toplam)}${s.rez_limit != null ? ` (limit ${para(s.rez_limit)})` : ''}`,
    okText: 'Gönder', cancelText: 'Vazgeç', className: 'hr-modal',
    onOk: () => api.post(`/${s.id}/ilerlet`).then(() => { message.success('Gönderildi.'); yukle(); })
      .catch((e) => message.error(hata(e))),
  });
  const planaGeri = () => api.post(`/${neden.seyahat.id}/plana-geri`, { neden: neden.metin.trim() })
    .then(() => { message.success('Plan personele geri gönderildi.'); setNeden({ seyahat: null, metin: '' }); yukle(); })
    .catch((e) => message.error(hata(e)));

  const q = ara.trim().toLocaleLowerCase('tr');
  const gorunen = (liste || []).filter((s) => !q || [s.personel_adi, s.il_ad, s.aciklama, s.amir_adi]
    .some((v) => (v || '').toLocaleLowerCase('tr').includes(q)));
  const bekleyenBelge = gorunen.reduce((a, s) => a + (s.rez_belge_bekleyen?.length || 0), 0);

  return (
    <div style={{
      '--rz-kenar': token.colorBorderSecondary, '--rz-yuzey': token.colorBgContainer, '--rz-baslik': token.colorFillAlter,
      '--rz-ikincil': token.colorTextSecondary, '--rz-hata': token.colorError,
    }}>
      <style>{`
        .rz-ust { display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
        .rz-kart { border: 1px solid var(--rz-kenar); border-radius: 12px; background: var(--rz-yuzey); margin-bottom: 16px; overflow: hidden; }
        .rz-bas { display: flex; justify-content: space-between; gap: 12px 16px; flex-wrap: wrap; padding: 14px 20px;
                  background: var(--rz-baslik); border-bottom: 1px solid var(--rz-kenar); }
        .rz-kisi { font-size: 16px; font-weight: 600; }
        .rz-il { font-size: 16px; color: var(--rz-ikincil); }
        .rz-meta { color: var(--rz-ikincil); font-size: 13px; margin-top: 4px; }
        .rz-sag { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; }
        .rz-govde { padding: 12px 20px 8px; }
        .rz-alt { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; padding: 4px 0 8px; }
        @media (max-width: 640px) {
          .rz-bas, .rz-govde { padding-left: 12px; padding-right: 12px; }
          .rz-sag { align-items: flex-start; }
        }
      `}</style>
      <div className="rz-ust">
        <Space wrap>
          <Segmented value={kapsam} onChange={setKapsam} options={[
            { value: 'acik', label: 'İşimde (Fiyat / Ödeme)' }, { value: 'onayda', label: 'Onay Sürecinde' },
            { value: 'kapali', label: 'Tamamlanan' },
          ]} />
          <Input.Search allowClear placeholder="Kişi, İl, Amir Ara" style={{ width: 240 }} value={ara} onChange={(e) => setAra(e.target.value)} />
        </Space>
        <Space wrap>
          {liste && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {gorunen.length} Seyahat{bekleyenBelge ? ` · ${bekleyenBelge} Belge Bekleniyor` : ''}
            </Text>
          )}
        </Space>
      </div>
      {!liste ? <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>
        : gorunen.length ? gorunen.map((s) => (
          <RezervasyonKarti key={s.id} s={s}
            onEkle={(sy, tur, ek) => setGiderIstek({ seyahat: sy, tur, gece: ek?.gece })}
            onDuzenle={(sy, gd) => setGiderIstek({ seyahat: sy, tur: gd.tur, gider: gd })}
            onAyrinti={(sy) => setPencere({ acik: true, id: sy.id })}
            onIlerlet={ilerlet} onPlanaGeri={(sy) => setNeden({ seyahat: sy, metin: '' })} />
        )) : <Empty description={{ acik: 'Fiyat ya da ödeme bekleyen seyahat yok', onayda: 'Onay sürecinde seyahat yok',
          kapali: 'Son 120 günde tamamlanan seyahat yok' }[kapsam]} />}
      <Modal rootClassName="hr-modal" open={!!neden.seyahat} title="Plana Geri Gönder" okText="Geri Gönder" cancelText="Vazgeç"
        okButtonProps={{ disabled: neden.metin.trim().length < 3, danger: true }} onOk={planaGeri}
        onCancel={() => setNeden({ seyahat: null, metin: '' })} destroyOnHidden>
        <p>Plan personele döner; düzeltip yeniden amire gönderir. Girdiğiniz fiyatlar korunur.</p>
        <Input.TextArea rows={3} maxLength={500} value={neden.metin} placeholder="Neden (zorunlu): ör. otel bulunamadı, tarihleri değiştirin"
          onChange={(e) => setNeden((n) => ({ ...n, metin: e.target.value }))} />
      </Modal>
      <GiderPenceresi istek={giderIstek} okumaAcik kok="/harcirah-rezervasyon" belgeZorunlu={false}
        onKapat={() => setGiderIstek(null)} onKaydedildi={(s) => { guncelle(s); setGiderIstek(null); }} />
      <SeyahatPenceresi acik={pencere.acik} seyahatId={pencere.id} secenekler={secenekler} yazabilir={false}
        onKapat={() => setPencere({ acik: false, id: null })}
        onKaydedildi={() => { setPencere({ acik: false, id: null }); yukle(); }} />
    </div>
  );
}

function RezervasyonSekmeleri() {
  const api = useHarcirahApi('/harcirah-rezervasyon');
  const [secenekler, setSecenekler] = useState(null);
  useEffect(() => { api.get('/secenekler').then(setSecenekler).catch(() => {}); }, [api]);
  return (
    <Tabs type="card" destroyOnHidden items={[
      { key: 'liste', label: 'Seyahatler Ve Rezervasyonlar', children: <RezervasyonListesi secenekler={secenekler} /> },
      { key: 'plan', label: 'Haftalık Plan', children: <HaftalikPlan secenekler={secenekler} yazabilir={false} /> },
    ]} />
  );
}

export default function HarcirahRezervasyon() {
  return (
    <HarcirahKabuk baslik="Rezervasyon">
      {(r) => (r.rezervasyon ? <RezervasyonSekmeleri />
        : <Result status="403" title="Bu ekran size kapalı" subTitle="Rezervasyon ekranı rezervasyon personeline açıktır." />)}
    </HarcirahKabuk>
  );
}
